import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton, inputCls, labelCls } from "./TenantShell";
import { tapi, apiErr } from "../../lib/tenant";

const SECTORS = ["Agriculture & Agribusiness", "Retail & Wholesale Trade", "Manufacturing", "Transport & Logistics", "Construction & Real Estate", "Hospitality & Tourism", "Healthcare", "Education", "ICT & Fintech", "Professional Services", "Energy & Water", "Other"];
const COUNTRIES = ["Kenya", "Uganda", "Tanzania", "Rwanda", "Ethiopia", "Nigeria", "Ghana", "South Africa", "Other"];

const YesNo = ({ label, value, onChange, testid, hint }) => (
  <div>
    <span className={labelCls}>{label}</span>
    <div className="flex gap-2" data-testid={testid}>
      {[["yes", true], ["no", false]].map(([k, v]) => (
        <button key={k} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${k}`}
          style={value === v ? { background: "var(--tenant)", borderColor: "var(--tenant)" } : undefined}
          className={"rounded-full border px-4 py-1.5 text-xs font-bold capitalize " + (value === v ? "text-white" : "border-neutral-300 bg-white text-neutral-600")}>{k}</button>
      ))}
    </div>
    {hint && <p className="mt-1 text-[11px] text-neutral-400">{hint}</p>}
  </div>
);

export const StepBusiness = ({ initial, onSaved }) => {
  const { sub } = useTenant();
  const [f, setF] = useState(() => ({
    business_name: "", sector: "", country: "Kenya", years_trading: "", registered: true, monthly_revenue_kes: "",
    existing_loans: false, crb_listed: false, tax_compliant: true, litigation: false, loan_amount_kes: "", loan_purpose: "", description: "",
    ...(initial || {}),
  }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setV = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...f, years_trading: Number(f.years_trading), monthly_revenue_kes: Number(f.monthly_revenue_kes), loan_amount_kes: Number(f.loan_amount_kes) };
      await tapi.put(`/t/${sub}/business`, body);
      toast.success("Business details saved");
      onSaved();
    } catch (err) { toast.error(apiErr(err, "Couldn't save your details")); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="tenant-step-business">
      <div><h2 className="text-lg font-extrabold">Tell us about your business</h2><p className="text-sm text-neutral-500">Honest answers help us give you an accurate outcome. Nothing here is shared until you ask to be connected.</p></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block sm:col-span-2"><span className={labelCls}>Business name</span><input required value={f.business_name} onChange={set("business_name")} className={inputCls} data-testid="biz-name" /></label>
        <label className="block"><span className={labelCls}>Sector</span>
          <select required value={f.sector} onChange={set("sector")} className={inputCls} data-testid="biz-sector"><option value="">Select…</option>{SECTORS.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label className="block"><span className={labelCls}>Country</span>
          <select required value={f.country} onChange={set("country")} className={inputCls} data-testid="biz-country">{COUNTRIES.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label className="block"><span className={labelCls}>Years trading</span><input required type="number" min={0} step={0.5} value={f.years_trading} onChange={set("years_trading")} className={inputCls} data-testid="biz-years" /></label>
        <label className="block"><span className={labelCls}>Average monthly revenue (KES)</span><input required type="number" min={0} step="any" value={f.monthly_revenue_kes} onChange={set("monthly_revenue_kes")} className={inputCls} data-testid="biz-revenue" /></label>
        <label className="block"><span className={labelCls}>Loan amount sought (KES)</span><input required type="number" min={1} step="any" value={f.loan_amount_kes} onChange={set("loan_amount_kes")} className={inputCls} data-testid="biz-loan-amount" /></label>
        <label className="block"><span className={labelCls}>What will the loan be used for?</span><input required value={f.loan_purpose} onChange={set("loan_purpose")} className={inputCls} placeholder="e.g. Stock for the December season" data-testid="biz-purpose" /></label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <YesNo label="Is the business formally registered?" value={f.registered} onChange={setV("registered")} testid="biz-registered" />
        <YesNo label="Do you hold a valid tax compliance certificate?" value={f.tax_compliant} onChange={setV("tax_compliant")} testid="biz-tax" />
        <YesNo label="Do you have any existing loans?" value={f.existing_loans} onChange={setV("existing_loans")} testid="biz-loans" />
        <YesNo label="Any negative CRB / credit bureau listing?" value={f.crb_listed} onChange={setV("crb_listed")} testid="biz-crb" />
        <YesNo label="Any ongoing litigation involving the business?" value={f.litigation} onChange={setV("litigation")} testid="biz-litigation" />
      </div>
      <label className="block"><span className={labelCls}>Briefly describe what your business does (optional)</span>
        <textarea rows={3} value={f.description} onChange={set("description")} className={inputCls} data-testid="biz-description" /></label>
      <div className="flex justify-end"><BrandButton type="submit" disabled={busy} data-testid="biz-submit">{busy && <Loader2 size={14} className="animate-spin" />} Save and continue <ArrowRight size={16} /></BrandButton></div>
    </form>
  );
};
